import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface DiscoveredBacklink {
  id: string;
  client_id: string;
  source_url: string;
  source_name: string;
  status: string;
  domain_authority: number | null;
  found_at: string;
}

const statusStyles: Record<string, string> = {
  verified: "bg-[#00ff88]/10 text-[#00ff88]/80",
  submitted: "bg-blue-500/10 text-blue-400/80",
  pending: "bg-amber-500/10 text-amber-400/80",
};

const BacklinksTab = ({ clientIds }: { clientIds: string[] }) => {
  const [backlinks, setBacklinks] = useState<DiscoveredBacklink[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    if (clientIds.length === 0) {
      setBacklinks([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("discovered_backlinks")
      .select("*")
      .in("client_id", clientIds)
      .order("domain_authority", { ascending: false, nullsFirst: false });
    if (data) setBacklinks(data as DiscoveredBacklink[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds.join(",")]);

  const handleRefresh = async () => {
    if (clientIds.length === 0) return;
    setRefreshing(true);
    try {
      const results = await Promise.all(
        clientIds.map((cid) =>
          supabase.functions.invoke("fetch-backlinks", { body: { client_id: cid } })
        )
      );
      const failures = results.filter((r) => r.error || !r.data?.success);
      if (failures.length === results.length) {
        toast({
          title: "Could not fetch backlinks",
          description: failures[0].data?.error || failures[0].error?.message || "Unknown error",
          variant: "destructive",
        });
      } else {
        toast({ title: "Backlinks refreshed" });
        await load();
      }
    } catch (err: any) {
      toast({ title: "Refresh failed", description: err.message, variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-14 bg-white/[0.02] rounded-lg animate-pulse border border-white/5" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider">Backlinks</p>
          <p className="text-2xl font-mono text-white/90 mt-1">
            {backlinks.length}{" "}
            <span className="text-xs text-white/40">total linking domains</span>
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing || clientIds.length === 0}
          className="text-xs font-mono px-3 py-1.5 rounded-md border border-[#00ff88]/30 bg-[#00ff88]/5 text-[#00ff88] hover:bg-[#00ff88]/10 transition-colors disabled:opacity-50"
        >
          {refreshing ? "Refreshing…" : "↻ Refresh"}
        </button>
      </div>

      {/* List */}
      {backlinks.length === 0 ? (
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 text-center">
          <p className="text-sm text-white/50 font-mono">No backlinks discovered yet.</p>
          <p className="text-xs text-white/20 font-mono mt-2">
            Click Refresh to fetch the latest data from Open PageRank.
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {backlinks.map((bl, i) => (
            <motion.div
              key={bl.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.02 }}
              className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-3 flex items-center justify-between gap-2"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white/80 font-mono truncate">{bl.source_name}</p>
                <a
                  href={bl.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] font-mono text-white/25 hover:text-white/40 truncate block mt-0.5"
                >
                  {bl.source_url}
                </a>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {bl.domain_authority !== null && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/5 text-white/60">
                    DA {Number(bl.domain_authority).toFixed(1)}
                  </span>
                )}
                <span
                  className={`text-[10px] font-mono px-2 py-0.5 rounded-full capitalize ${
                    statusStyles[bl.status] || statusStyles.pending
                  }`}
                >
                  {bl.status}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BacklinksTab;
